import { useJsonFormatter } from '../../hooks/useJsonFormatter';

export interface JsonFormatButtonProps {
	/**
	 * The content to format
	 */
	content: string;

	/**
	 * Callback when formatting is successful
	 */
	onFormatSuccess: (formattedContent: string) => void;

	/**
	 * Callback when formatting fails
	 */
	onFormatError?: (error: string) => void;

	/**
	 * Custom button text
	 */
	buttonText?: string;

	/**
	 * Custom button size
	 */
	size?: 'sm' | 'md' | 'lg';

	/**
	 * Custom button variant
	 */
	variant?: 'primary' | 'secondary' | 'outline';

	/**
	 * Whether to show loading state
	 */
	showLoading?: boolean;

	/**
	 * Custom CSS classes
	 */
	className?: string;

	/**
	 * Button title/tooltip
	 */
	title?: string;

	/**
	 * Whether the button is disabled
	 */
	disabled?: boolean;
}

/**
 * Reusable JSON formatting button component
 * Automatically formats JSON content and provides user feedback
 */
export function JsonFormatButton({
	content,
	onFormatSuccess,
	onFormatError,
	buttonText = '🔧 Format JSON',
	size = 'md',
	variant = 'primary',
	showLoading = true,
	className = '',
	title = 'Format and validate JSON content',
	disabled = false,
}: JsonFormatButtonProps) {
	const { formatJson, isFormatting, lastResult } = useJsonFormatter();

	const handleClick = async () => {
		if (!content.trim()) {
			return;
		}

		const result = await formatJson(content);

		if (result.success && result.formatted) {
			onFormatSuccess(result.formatted);
		} else if (result.error && onFormatError) {
			onFormatError(result.error);
		}
	};

	// Determine button size classes
	const sizeClasses = {
		sm: 'px-2 py-1 text-xs',
		md: 'px-3 py-1 text-sm',
		lg: 'px-4 py-2 text-base',
	};

	// Determine button variant classes
	const variantClasses = {
		primary:
			'text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700',
		secondary:
			'text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-700',
		outline:
			'text-blue-600 bg-transparent border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/20',
	};

	// Show success state briefly if formatting was successful
	const isSuccess = lastResult?.success && !isFormatting;
	const successClasses =
		'text-green-700 bg-green-100 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700';

	return (
		<button
			onClick={handleClick}
			disabled={disabled || isFormatting}
			className={`
				${sizeClasses[size]}
				border rounded transition-colors font-medium
				${isSuccess ? successClasses : variantClasses[variant]}
				${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
				${className}
			`.trim()}
			title={title}
		>
			{isFormatting && showLoading ? (
				<span className="flex items-center gap-1">
					<svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
						<circle
							className="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							strokeWidth="4"
							fill="none"
						/>
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
					Formatting...
				</span>
			) : isSuccess ? (
				'✅ Formatted!'
			) : (
				buttonText
			)}
		</button>
	);
}
